import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  FeatureAnnouncement,
  FeatureAnnouncementBanner,
  FeatureAnnouncementBadge,
  FeatureAnnouncementInline,
} from '../components/FeatureAnnouncement'
import { Box } from '@mui/material'

interface FeatureAnnouncementContextType {
  announcements: FeatureAnnouncement[]
  addAnnouncement: (announcement: FeatureAnnouncement) => void
  removeAnnouncement: (id: string) => void
  clearAll: () => void
}

const FeatureAnnouncementContext = createContext<FeatureAnnouncementContextType | undefined>(
  undefined
)

export const useFeatureAnnouncements = () => {
  const context = useContext(FeatureAnnouncementContext)
  if (!context) {
    throw new Error('useFeatureAnnouncements must be used within FeatureAnnouncementProvider')
  }
  return context
}

interface FeatureAnnouncementProviderProps {
  children: ReactNode
  initialAnnouncements?: FeatureAnnouncement[]
}

export function FeatureAnnouncementProvider({
  children,
  initialAnnouncements = [],
}: FeatureAnnouncementProviderProps) {
  const location = useLocation()
  const isOnboarding = location.pathname.includes('/onboarding')
  // Hide announcements when running Cypress tests
  const isCypressTest = typeof window !== 'undefined' && (window as any).Cypress
  const [announcements, setAnnouncements] = useState<FeatureAnnouncement[]>(initialAnnouncements)

  const addAnnouncement = useCallback((announcement: FeatureAnnouncement) => {
    setAnnouncements((prev) => {
      // Don't add if already exists
      if (prev.some((a) => a.id === announcement.id)) {
        return prev
      }
      return [...prev, announcement]
    })
  }, [])

  const removeAnnouncement = useCallback((id: string) => {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const clearAll = useCallback(() => {
    setAnnouncements([])
  }, [])

  return (
    <FeatureAnnouncementContext.Provider
      value={{
        announcements,
        addAnnouncement,
        removeAnnouncement,
        clearAll,
      }}
    >
      {children}
      {/* Render announcements - hide on onboarding screens and during Cypress tests */}
      {!isOnboarding && !isCypressTest && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 13000,
            pointerEvents: 'none',
            '& > *': {
              pointerEvents: 'auto',
            },
          }}
        >
          <Box
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              px: 2,
              pt: 2,
            }}
          >
            {announcements
              .filter((a) => a.type === 'banner')
              .map((announcement) => (
                <FeatureAnnouncementBanner
                  key={announcement.id}
                  announcement={announcement}
                  onDismiss={removeAnnouncement}
                />
              ))}
          </Box>
        </Box>
      )}
    </FeatureAnnouncementContext.Provider>
  )
}

